'use client';

import React, { createContext, useContext, useState, ReactNode } from 'react';

export type UserRole = 'super_admin' | 'payroll_manager' | 'coordinator' | 'employee';

export interface RoleUser {
  name: string;
  initials: string;
  role: UserRole;
  roleLabel: string;
  employeeId?: string;
}

export const ROLE_USERS: Record<UserRole, RoleUser> = {
  super_admin: {
    name: 'Sarah Reynolds',
    initials: 'SR',
    role: 'super_admin',
    roleLabel: 'Super Admin',
  },
  payroll_manager: {
    name: 'Marcus Thompson',
    initials: 'MT',
    role: 'payroll_manager',
    roleLabel: 'Payroll Manager',
  },
  coordinator: {
    name: 'Priya Nair',
    initials: 'PN',
    role: 'coordinator',
    roleLabel: 'Coordinator',
  },
  employee: {
    name: 'Amara Okafor',
    initials: 'AO',
    role: 'employee',
    roleLabel: 'Employee',
    employeeId: 'pr-w18-001',
  },
};

export interface RolePermissions {
  // Dashboard
  canViewDashboard: boolean;
  canViewKPIs: boolean;
  canViewCharts: boolean;
  canViewActivityFeed: boolean;
  canViewQuickActions: boolean;
  canViewComplianceAlerts: boolean;
  // Time Clock
  canViewTimeClock: boolean;
  canApproveTimesheets: boolean;
  canRejectTimesheets: boolean;
  canManualEntry: boolean;
  canExportTimesheets: boolean;
  canViewAllTimesheets: boolean;
  canViewOwnTimesheets: boolean;
  // Payroll
  canViewPayroll: boolean;
  canViewAllPayroll: boolean;
  canViewOwnPayslip: boolean;
  canRunPayroll: boolean;
  canDownloadPayslips: boolean;
  canSubmitRTI: boolean;
  canExportPayroll: boolean;
  canEditPayrollEntries: boolean;
  // Navigation
  canAccessWorkers: boolean;
  canAccessLeave: boolean;
  canAccessReports: boolean;
  canAccessCompliance: boolean;
  canAccessSettings: boolean;
  // Admin
  canManageUsers: boolean;
  canManageOrganisation: boolean;
  canViewAuditLog: boolean;
}

const PERMISSIONS: Record<UserRole, RolePermissions> = {
  super_admin: {
    canViewDashboard: true,
    canViewKPIs: true,
    canViewCharts: true,
    canViewActivityFeed: true,
    canViewQuickActions: true,
    canViewComplianceAlerts: true,
    canViewTimeClock: true,
    canApproveTimesheets: true,
    canRejectTimesheets: true,
    canManualEntry: true,
    canExportTimesheets: true,
    canViewAllTimesheets: true,
    canViewOwnTimesheets: true,
    canViewPayroll: true,
    canViewAllPayroll: true,
    canViewOwnPayslip: true,
    canRunPayroll: true,
    canDownloadPayslips: true,
    canSubmitRTI: true,
    canExportPayroll: true,
    canEditPayrollEntries: true,
    canAccessWorkers: true,
    canAccessLeave: true,
    canAccessReports: true,
    canAccessCompliance: true,
    canAccessSettings: true,
    canManageUsers: true,
    canManageOrganisation: true,
    canViewAuditLog: true,
  },
  payroll_manager: {
    canViewDashboard: true,
    canViewKPIs: true,
    canViewCharts: true,
    canViewActivityFeed: true,
    canViewQuickActions: true,
    canViewComplianceAlerts: true,
    canViewTimeClock: true,
    canApproveTimesheets: true,
    canRejectTimesheets: true,
    canManualEntry: true,
    canExportTimesheets: true,
    canViewAllTimesheets: true,
    canViewOwnTimesheets: true,
    canViewPayroll: true,
    canViewAllPayroll: true,
    canViewOwnPayslip: true,
    canRunPayroll: true,
    canDownloadPayslips: true,
    canSubmitRTI: true,
    canExportPayroll: true,
    canEditPayrollEntries: true,
    canAccessWorkers: true,
    canAccessLeave: true,
    canAccessReports: true,
    canAccessCompliance: true,
    canAccessSettings: false,
    canManageUsers: false,
    canManageOrganisation: false,
    canViewAuditLog: false,
  },
  coordinator: {
    canViewDashboard: true,
    canViewKPIs: true,
    canViewCharts: false,
    canViewActivityFeed: true,
    canViewQuickActions: false,
    canViewComplianceAlerts: true,
    canViewTimeClock: true,
    canApproveTimesheets: false,
    canRejectTimesheets: false,
    canManualEntry: false,
    canExportTimesheets: false,
    canViewAllTimesheets: true,
    canViewOwnTimesheets: true,
    canViewPayroll: false,
    canViewAllPayroll: false,
    canViewOwnPayslip: true,
    canRunPayroll: false,
    canDownloadPayslips: false,
    canSubmitRTI: false,
    canExportPayroll: false,
    canEditPayrollEntries: false,
    canAccessWorkers: true,
    canAccessLeave: true,
    canAccessReports: false,
    canAccessCompliance: false,
    canAccessSettings: false,
    canManageUsers: false,
    canManageOrganisation: false,
    canViewAuditLog: false,
  },
  employee: {
    canViewDashboard: false,
    canViewKPIs: false,
    canViewCharts: false,
    canViewActivityFeed: false,
    canViewQuickActions: false,
    canViewComplianceAlerts: false,
    canViewTimeClock: false,
    canApproveTimesheets: false,
    canRejectTimesheets: false,
    canManualEntry: false,
    canExportTimesheets: false,
    canViewAllTimesheets: false,
    canViewOwnTimesheets: true,
    canViewPayroll: false,
    canViewAllPayroll: false,
    canViewOwnPayslip: true,
    canRunPayroll: false,
    canDownloadPayslips: true,
    canSubmitRTI: false,
    canExportPayroll: false,
    canEditPayrollEntries: false,
    canAccessWorkers: false,
    canAccessLeave: false,
    canAccessReports: false,
    canAccessCompliance: false,
    canAccessSettings: false,
    canManageUsers: false,
    canManageOrganisation: false,
    canViewAuditLog: false,
  },
};

interface RoleContextValue {
  currentUser: RoleUser;
  permissions: RolePermissions;
  setRole: (role: UserRole) => void;
  hasPermission: (permission: keyof RolePermissions) => boolean;
}

const RoleContext = createContext<RoleContextValue | null>(null);

export function RoleProvider({ children }: { children: ReactNode }) {
  const [currentRole, setCurrentRole] = useState<UserRole>('super_admin');

  const currentUser = ROLE_USERS[currentRole];
  const permissions = PERMISSIONS[currentRole];

  const setRole = (role: UserRole) => setCurrentRole(role);

  const hasPermission = (permission: keyof RolePermissions) => permissions[permission];

  return (
    <RoleContext.Provider value={{ currentUser, permissions, setRole, hasPermission }}>
      {children}
    </RoleContext.Provider>
  );
}

export function useRole() {
  const ctx = useContext(RoleContext);
  if (!ctx) throw new Error('useRole must be used within RoleProvider');
  return ctx;
}
